export class TouchOverlay {
    options;
    now;
    coarsePointer;
    hostWindow;
    hostDocument;
    actions = {};
    inputs;
    touch;
    enabled = true;
    suppressedUntil = -Infinity;
    showTimer;
    keepaliveTimer;
    constructor(options) {
        this.options = options;
        this.inputs = options.inputs;
        this.touch = options.touch;
        this.now = options.now ?? (() => performance.now());
        this.hostWindow = options.window ?? window;
        this.hostDocument = options.document ?? document;
        this.coarsePointer =
            options.coarsePointer ?? (() => this.hostWindow.matchMedia?.('(pointer: coarse)').matches ?? false);
        Object.assign(options.layer.style, { position: 'absolute', inset: '0', pointerEvents: 'none' });
        this.hostWindow.addEventListener('keydown', this.onKeydown);
        this.hostDocument.addEventListener('gesturestart', this.preventGesture, { passive: false });
        this.render();
    }
    setInputs(inputs, touch) {
        this.deactivate(true);
        this.inputs = inputs;
        this.touch = touch;
        for (const action of Object.keys(this.actions))
            delete this.actions[action];
        this.render();
    }
    setEnabled(enabled) {
        if (this.enabled && !enabled)
            this.deactivate(true);
        this.enabled = enabled;
        this.refreshVisibility();
    }
    noteGamepadInput(at = this.now()) {
        this.suppress(at);
    }
    noteKeyboardInput(at = this.now()) {
        this.suppress(at);
    }
    dispose() {
        this.deactivate(false);
        this.hostWindow.removeEventListener('keydown', this.onKeydown);
        this.hostDocument.removeEventListener('gesturestart', this.preventGesture);
        if (this.showTimer)
            clearTimeout(this.showTimer);
        if (this.keepaliveTimer)
            clearInterval(this.keepaliveTimer);
        this.options.layer.replaceChildren();
    }
    render() {
        const layer = this.options.layer;
        layer.replaceChildren();
        const entries = Object.entries(this.inputs.actions);
        if (this.touch === 'tap')
            return this.refreshVisibility();
        const sticks = entries.filter(([, kind]) => kind === 'stick').slice(0, this.touch === 'two-stick' ? 2 : 1);
        sticks.forEach(([action], index) => layer.append(this.createStick(action, index)));
        const buttons = entries.filter(([, kind]) => kind === 'button').slice(0, 5);
        if (entries.filter(([, kind]) => kind === 'button').length > 5) {
            console.warn('TouchOverlay supports at most five button actions');
        }
        buttons.forEach(([action], index) => layer.append(this.createButton(action, index)));
        this.refreshVisibility();
    }
    createStick(action, index) {
        const stick = this.hostDocument.createElement('div');
        stick.dataset.stick = action;
        stick.setAttribute('role', 'button');
        stick.setAttribute('aria-label', action);
        Object.assign(stick.style, {
            position: 'absolute',
            left: index === 0 ? '24px' : 'auto',
            right: index === 1 ? '24px' : 'auto',
            bottom: '24px',
            width: '100px',
            height: '100px',
            borderRadius: '50%',
            pointerEvents: 'auto',
            touchAction: 'none',
        });
        const nub = this.hostDocument.createElement('span');
        nub.style.display = 'block';
        nub.style.width = '44px';
        nub.style.height = '44px';
        nub.style.margin = '28px';
        nub.style.borderRadius = '50%';
        stick.append(nub);
        let pointerId;
        let startX = 0;
        let startY = 0;
        const move = (event) => {
            let x = event.clientX - startX;
            let y = event.clientY - startY;
            const magnitude = Math.hypot(x, y);
            if (magnitude > 50) {
                x *= 50 / magnitude;
                y *= 50 / magnitude;
            }
            this.actions[action] = { x: x / 50, y: y / 50 };
            nub.style.transform = `translate(${x}px, ${y}px)`;
            this.emit();
        };
        stick.addEventListener('pointerdown', (event) => {
            pointerId = event.pointerId;
            startX = event.clientX;
            startY = event.clientY;
            try {
                stick.setPointerCapture(pointerId);
            }
            catch {
                // Some embedded browsers expose PointerEvent without pointer capture.
            }
            this.actions[action] = { x: 0, y: 0 };
            this.startKeepalive();
            this.emit();
        });
        stick.addEventListener('pointermove', (event) => {
            if (event.pointerId === pointerId)
                move(event);
        });
        const release = (event) => {
            if (event.pointerId !== pointerId)
                return;
            pointerId = undefined;
            this.actions[action] = { x: 0, y: 0 };
            nub.style.transform = '';
            this.emit();
            this.stopKeepaliveIfIdle();
        };
        stick.addEventListener('pointerup', release);
        stick.addEventListener('pointercancel', release);
        stick.addEventListener('touchstart', (event) => event.preventDefault(), { passive: false });
        return stick;
    }
    createButton(action, index) {
        const button = this.hostDocument.createElement('button');
        button.type = 'button';
        button.textContent = action;
        button.setAttribute('aria-label', action);
        const size = index === 0 ? 64 : 44;
        Object.assign(button.style, {
            position: 'absolute',
            right: `${24 + (index % 2) * 56}px`,
            bottom: `${24 + Math.floor(index / 2) * 56}px`,
            width: `${size}px`,
            height: `${size}px`,
            borderRadius: '50%',
            pointerEvents: 'auto',
            touchAction: 'none',
        });
        button.addEventListener('pointerdown', (event) => {
            event.preventDefault();
            try {
                button.setPointerCapture?.(event.pointerId);
            }
            catch {
                // Match the stick fallback for browsers with partial pointer capture.
            }
            this.actions[action] = true;
            this.startKeepalive();
            this.emit();
        });
        const release = () => {
            this.actions[action] = false;
            this.emit();
            this.stopKeepaliveIfIdle();
        };
        button.addEventListener('pointerup', release);
        button.addEventListener('pointercancel', release);
        button.addEventListener('touchstart', (event) => event.preventDefault(), { passive: false });
        return button;
    }
    emit() {
        if (!this.enabled || this.now() < this.suppressedUntil)
            return;
        this.options.onFrame({
            playerIndex: this.options.playerIndex,
            tick: Math.max(0, Math.floor(this.now())),
            source: 'touch',
            actions: { ...this.actions },
        });
    }
    suppress(at) {
        this.deactivate(true);
        this.suppressedUntil = Math.max(this.suppressedUntil, at + 2_000);
        if (this.showTimer)
            clearTimeout(this.showTimer);
        this.showTimer = setTimeout(() => this.refreshVisibility(), Math.max(0, this.suppressedUntil - this.now()));
        this.refreshVisibility();
    }
    refreshVisibility() {
        const hidden = !this.enabled || !this.coarsePointer() || this.now() < this.suppressedUntil;
        this.options.layer.hidden = hidden;
        this.options.layer.setAttribute('aria-hidden', String(hidden));
    }
    startKeepalive() {
        if (this.keepaliveTimer)
            return;
        this.keepaliveTimer = setInterval(() => this.emit(), 250);
    }
    deactivate(emitReset) {
        let active = false;
        for (const [action, value] of Object.entries(this.actions)) {
            if (typeof value === 'boolean') {
                active ||= value;
                this.actions[action] = false;
            }
            else if (typeof value === 'number') {
                active ||= value !== 0;
                this.actions[action] = 0;
            }
            else {
                active ||= value.x !== 0 || value.y !== 0;
                this.actions[action] = { x: 0, y: 0 };
            }
        }
        if (this.keepaliveTimer) {
            clearInterval(this.keepaliveTimer);
            this.keepaliveTimer = undefined;
        }
        if (active && emitReset)
            this.emit();
    }
    stopKeepaliveIfIdle() {
        const active = Object.values(this.actions).some((value) => typeof value === 'boolean' ? value : typeof value === 'number' ? value !== 0 : value.x !== 0 || value.y !== 0);
        if (!active && this.keepaliveTimer) {
            clearInterval(this.keepaliveTimer);
            this.keepaliveTimer = undefined;
        }
    }
    onKeydown = () => this.noteKeyboardInput();
    preventGesture = (event) => event.preventDefault();
}
//# sourceMappingURL=overlay.js.map